import React, { useState } from 'react';
import { getYouTubeThumbnail, isYouTubeUrl } from 'helpers/youtubeUtils';

interface YouTubeThumbnailProps {
  url: string;
  thumbnail?: string | null;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  onClick?: () => void;
}

const YouTubeThumbnail: React.FC<YouTubeThumbnailProps> = ({
  url,
  thumbnail,
  alt = 'YouTube Video Thumbnail',
  className = '',
  style,
  onError,
  onClick
}) => {
  const [currentQuality, setCurrentQuality] = useState<
    'maxres' | 'hq' | 'mq' | 'sd' | 'default'
  >('maxres');
  const [hasErrored, setHasErrored] = useState(false);

  // Determine the thumbnail source
  const getThumbnailSource = (): string => {
    // Use provided thumbnail if available and valid
    if (thumbnail && thumbnail.trim() !== '') {
      return thumbnail;
    }

    // Generate thumbnail from YouTube URL if it's a YouTube URL
    if (isYouTubeUrl(url)) {
      return getYouTubeThumbnail(url, currentQuality);
    }

    // Return empty string if no valid source
    return '';
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const target = event.target as HTMLImageElement;

    // Try lower quality thumbnails if high quality fails
    if (currentQuality === 'maxres' && !hasErrored) {
      setCurrentQuality('hq');
      return;
    }

    if (currentQuality === 'hq' && !hasErrored) {
      setCurrentQuality('mq');
      return;
    }

    if (currentQuality === 'mq' && !hasErrored) {
      setCurrentQuality('sd');
      return;
    }

    if (currentQuality === 'sd' && !hasErrored) {
      setCurrentQuality('default');
      return;
    }

    // Mark as errored to prevent infinite loops
    setHasErrored(true);

    // Call the custom onError handler if provided
    if (onError) {
      onError(event);
    }
  };

  const thumbnailSrc = getThumbnailSource();

  // Don't render if no valid thumbnail source
  if (!thumbnailSrc) {
    return (
      <div
        className={`youtube-thumbnail-placeholder ${className}`}
        style={{
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
          ...style
        }}
        onClick={onClick}
      >
        No Thumbnail
      </div>
    );
  }

  return (
    <img
      src={thumbnailSrc}
      alt={alt}
      className={className}
      style={style}
      onError={handleImageError}
      onClick={onClick}
      loading="lazy"
    />
  );
};

export default YouTubeThumbnail;
